import React, { useEffect, useRef, useState } from "react";

import backend from "../../backend";
import useIsMobile from "../../hooks/useIsMobile";
import { formatItemSubId } from "../../utils";
import {
  expandWrap,
  filterRow,
  stateMessage,
  chipStyle,
  fmtDateTime,
} from "./ProduccionV2.detalles";

// ─── Hook: useProcessSuggestions ───────────────────────────────────────────
// Fetch 1× al mount, cache localStorage 5 min. Comparte con todas las instancias.
const SUG_CACHE_KEY = "produccion.processSuggestions.v1";
const SUG_TTL_MS = 5 * 60 * 1000;

export const useProcessSuggestions = (enabled = true) => {
  const [suggestions, setSuggestions] = useState(() => {
    try {
      const raw = localStorage.getItem(SUG_CACHE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!parsed?.expiresAt || parsed.expiresAt < Date.now()) return [];
      return parsed.data?.suggestions || [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (fetchedRef.current) return;
    if (suggestions.length > 0) return;

    fetchedRef.current = true;
    setLoading(true);
    backend
      .get("processSuggestions?limit=15")
      .then(({ data }) => {
        const sugs = data.suggestions || [];
        setSuggestions(sugs);
        try {
          localStorage.setItem(
            SUG_CACHE_KEY,
            JSON.stringify({ data, expiresAt: Date.now() + SUG_TTL_MS })
          );
        } catch {}
      })
      .catch((e) => console.warn("[useProcessSuggestions]", e))
      .finally(() => setLoading(false));
  }, [enabled, suggestions.length]);

  return { suggestions, loading };
};

// ─── DefinirProcesosForm (Vista A) ─────────────────────────────────────────
export const DefinirProcesosForm = ({ pi, onSaved, onCancel }) => {
  const { suggestions } = useProcessSuggestions(true);
  const [rows, setRows] = useState([
    { name: "", isExternal: false, externalProviderName: "", notes: "", theoreticalDuration: "", machineType: "" },
  ]);
  const [syncToCatalog, setSyncToCatalog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [activeAutocomplete, setActiveAutocomplete] = useState(null); // idx de la fila con dropdown abierto
  // Mapa name(lowercase) → duration (min) del catálogo de la pieza, para pre-llenar.
  const [catalogDurMap, setCatalogDurMap] = useState({});
  // Lista de tipos de máquina del catálogo (para dropdown opcional por fila).
  // Si Catalina selecciona una máquina + tilda "guardar en catálogo", el
  // Process queda con machineType seteada y P3 va a poder calcular.
  const [machineTypes, setMachineTypes] = useState([]);
  useEffect(() => {
    let cancelled = false;
    backend.get("machineType")
      .then(({ data }) => { if (!cancelled) setMachineTypes(data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const pieceId = pi?.piece?._id || pi?.piece;

  // Carga procesos del catálogo de la pieza para pre-llenar tiempo teórico
  // cuando el operador ya la fabricó antes (o alguien definió procesos en /pieces).
  useEffect(() => {
    if (!pieceId) return;
    let cancelled = false;
    backend
      .get(`piece/${pieceId}`)
      .then(({ data }) => {
        if (cancelled || !data?.processes) return;
        const m = {};
        for (const p of data.processes) {
          const k = (p.name || "").trim().toLowerCase();
          if (k && Number(p.duration) > 0) m[k] = Number(p.duration);
        }
        setCatalogDurMap(m);
        // Si las filas iniciales (vacías) coinciden con catálogo, pre-llenar
        setRows((prev) =>
          prev.map((r) => {
            if (!r.name || r.theoreticalDuration) return r;
            const hit = m[r.name.trim().toLowerCase()];
            return hit ? { ...r, theoreticalDuration: String(hit) } : r;
          })
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [pieceId]);

  const setRow = (i, patch) =>
    setRows((prev) =>
      prev.map((r, idx) => {
        if (idx !== i) return r;
        const next = { ...r, ...patch };
        // Si el nombre cambió y el catálogo tiene duración para ese proceso,
        // pre-llenar SIEMPRE que el campo esté vacío (no pisar lo que escribió el operador).
        if (patch.name !== undefined && !next.theoreticalDuration) {
          const hit = catalogDurMap[(patch.name || "").trim().toLowerCase()];
          if (hit) next.theoreticalDuration = String(hit);
        }
        return next;
      })
    );
  const addRow = () =>
    setRows((prev) => [
      ...prev,
      { name: "", isExternal: false, externalProviderName: "", notes: "", theoreticalDuration: "", machineType: "" },
    ]);
  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const canSync = !!pi?.piece;

  const handleSubmit = async () => {
    setError(null);
    const cleaned = rows.map((r) => {
      const td = parseInt((r.theoreticalDuration || "").toString(), 10);
      return {
        name: (r.name || "").trim(),
        isExternal: !!r.isExternal,
        externalProviderName: r.isExternal ? (r.externalProviderName || "").trim() : "",
        notes: (r.notes || "").trim(),
        theoreticalDuration: Number.isFinite(td) && td > 0 ? td : null,
        // machineType solo se manda cuando NO es externo y hay seleccion.
        // El backend lo usa al hacer sync al catálogo para que P3 calcule.
        machineType: !r.isExternal && r.machineType ? r.machineType : undefined,
      };
    });
    if (cleaned.some((r) => !r.name)) {
      setError("Todas las filas deben tener un nombre de proceso.");
      return;
    }
    // El tiempo teórico es OPCIONAL: Catalina registra el avance sin tenerlo.
    // Se completa después (cotización) o se calcula del histórico real que la
    // app captura sola (startedAt/completedAt). Ref: adopción producción.
    try {
      setSubmitting(true);
      const { data } = await backend.post(`pieceInstance/${pi._id}/processes`, {
        processes: cleaned,
        syncToCatalog: canSync && syncToCatalog,
      });
      if (typeof onSaved === "function") {
        onSaved(data.pieceInstance, data.catalogSynced, syncToCatalog && canSync);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || e.message || "Error al guardar";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredSuggestions = (query) => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return suggestions.slice(0, 8);
    return suggestions.filter((s) => s.name.toLowerCase().includes(q)).slice(0, 8);
  };

  const pieceName = pi?.piece?.name || pi?.pieceNameSnapshot || "esta pieza";

  return (
    <div style={{ padding: "12px 16px", borderTop: "1px dashed #2e2e2e", background: "#101010" }}>
      <div
        style={{
          fontSize: 10,
          color: "#8a8a8a",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: ".05em",
          marginBottom: 8,
        }}
      >
        Definir procesos para {pieceName}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((row, i) => (
          <div
            key={i}
            style={{
              background: "#141414",
              border: "1px solid #2e2e2e",
              borderRadius: 5,
              padding: 8,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" /* móvil: filas del form bajan de línea */ }}>
              <span style={{ fontSize: 11, color: "#aaa", width: 18, textAlign: "right", fontWeight: 600 }}>{i + 1}.</span>

              <div style={{ flex: 1, minWidth: 160, position: "relative" }}>
                <input
                  type="text"
                  value={row.name}
                  onChange={(e) => setRow(i, { name: e.target.value })}
                  onFocus={() => setActiveAutocomplete(i)}
                  onBlur={() => setTimeout(() => setActiveAutocomplete((a) => (a === i ? null : a)), 150)}
                  placeholder="Nombre del proceso (ej: torneado)"
                  style={{
                    width: "100%",
                    background: "#0d0d0d",
                    border: "1px solid #2e2e2e",
                    color: "#fff",
                    padding: "5px 8px",
                    fontSize: 12,
                    borderRadius: 3,
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                {activeAutocomplete === i && filteredSuggestions(row.name).length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 2px)",
                      left: 0,
                      right: 0,
                      background: "#0d0d0d",
                      border: "1px solid #2e2e2e",
                      borderRadius: 3,
                      maxHeight: 180,
                      overflowY: "auto",
                      zIndex: 10,
                    }}
                  >
                    {filteredSuggestions(row.name).map((s) => (
                      <div
                        key={s.name}
                        onMouseDown={(e) => {
                          // mousedown evita que blur dispare antes de aplicar
                          e.preventDefault();
                          setRow(i, { name: s.name });
                          setActiveAutocomplete(null);
                        }}
                        style={{
                          padding: "5px 8px",
                          fontSize: 11,
                          color: "#ccc",
                          borderBottom: "1px solid #1a1a1a",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span>{s.name}</span>
                        <span style={{ fontSize: 9, color: s.source === "seed" ? "#a78bfa" : "#666" }}>
                          {s.source === "seed" ? "sugerido" : `${s.count}×`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  whiteSpace: "nowrap",
                }}
                title={row.isExternal ? "Tiempo teórico (opcional) — días para externos" : "Tiempo teórico (opcional) — minutos para internos"}
              >
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={row.theoreticalDuration}
                  onChange={(e) => setRow(i, { theoreticalDuration: e.target.value.replace(/\D/g, "") })}
                  placeholder="opc."
                  style={{
                    width: 56,
                    background: "#0d0d0d",
                    border: "1px solid #2e2e2e",
                    color: "#fff",
                    padding: "5px 6px",
                    fontSize: 11,
                    borderRadius: 3,
                    outline: "none",
                    textAlign: "right",
                    boxSizing: "border-box",
                  }}
                />
                <span style={{ fontSize: 11, color: "#aaa" }}>
                  {row.isExternal ? "días" : "min"}
                </span>
              </div>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 11,
                  color: row.isExternal ? "#3498db" : "#bbb",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                <input
                  type="checkbox"
                  checked={row.isExternal}
                  onChange={(e) => setRow(i, { isExternal: e.target.checked })}
                  style={{ cursor: "pointer" }}
                />
                externo
              </label>

              {/* Dropdown tipo de máquina — solo para procesos internos.
                  Opcional. Si se selecciona + se sincroniza al catálogo,
                  el Process queda completo y P3 calcula. */}
              {!row.isExternal && (
                <select
                  value={row.machineType || ""}
                  onChange={(e) => setRow(i, { machineType: e.target.value })}
                  style={{
                    background: "#0d0d0d",
                    border: "1px solid #2e2e2e",
                    color: row.machineType ? "#fff" : "#999",
                    padding: "4px 6px",
                    fontSize: 11,
                    borderRadius: 3,
                    outline: "none",
                    cursor: "pointer",
                    minWidth: 110,
                  }}
                  title="Tipo de máquina (opcional). Necesario para calcular P3 si se sincroniza al catálogo."
                >
                  <option value="">tipo máquina…</option>
                  {machineTypes.map(mt => (
                    <option key={mt._id} value={mt._id}>{mt.type}</option>
                  ))}
                </select>
              )}

              {rows.length > 1 && (
                <button
                  onClick={() => removeRow(i)}
                  title="Eliminar fila"
                  style={{
                    background: "#1e1e1e",
                    border: "1px solid #333",
                    color: "#cbd5e1",
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 12,
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  ×
                </button>
              )}
            </div>

            {row.isExternal && (
              <input
                type="text"
                value={row.externalProviderName}
                onChange={(e) => setRow(i, { externalProviderName: e.target.value })}
                placeholder="Proveedor externo (ej: Mecánica X)"
                style={{
                  width: "100%",
                  background: "#0d0d0d",
                  border: "1px solid #2e2e2e",
                  color: "#fff",
                  padding: "5px 8px",
                  fontSize: 11,
                  borderRadius: 3,
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            )}

            <input
              type="text"
              value={row.notes}
              onChange={(e) => setRow(i, { notes: e.target.value })}
              placeholder="Notas / observaciones (opcional)"
              style={{
                width: "100%",
                background: "#0d0d0d",
                border: "1px solid #2e2e2e",
                color: "#aaa",
                padding: "5px 8px",
                fontSize: 11,
                borderRadius: 3,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
        ))}
      </div>

      <button
        onClick={addRow}
        style={{
          marginTop: 8,
          background: "transparent",
          border: "1px dashed #4a4a4a",
          color: "#bbb",
          padding: "5px 10px",
          fontSize: 11,
          borderRadius: 6,
          cursor: "pointer",
          width: "100%",
          fontWeight: 500,
        }}
      >
        + agregar proceso
      </button>

      {canSync && (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 10,
            fontSize: 11,
            color: "#cbd5e1",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={syncToCatalog}
            onChange={(e) => setSyncToCatalog(e.target.checked)}
            style={{ cursor: "pointer" }}
          />
          guardar también en el catálogo de la pieza para próximas OTs
        </label>
      )}

      {error && (
        <div style={{ marginTop: 8, fontSize: 11, color: "#e74c3c" }}>{error}</div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            background: submitting ? "#1f1f1f" : "#27ae60",
            color: submitting ? "#666" : "#fff",
            border: "none",
            borderRadius: 6,
            padding: "6px 14px",
            fontSize: 11,
            fontWeight: 700,
            cursor: submitting ? "wait" : "pointer",
          }}
        >
          {submitting ? "Guardando…" : "Guardar pipeline"}
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={submitting}
            style={{
              background: "#1e1e1e",
              color: "#cbd5e1",
              border: "1px solid #333",
              borderRadius: 6,
              padding: "6px 14px",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            Cancelar
          </button>
        )}
      </div>
    </div>
  );
};

// ─── SyncCatalogoPrompt (Vista B) ──────────────────────────────────────────
// Banner informativo post-save. Auto-cierra. NO hace acciones — el sync ya pasó (o no) en POST.
// Si Catalina no tildó el checkbox, se le sugiere para próxima vez.
export const SyncCatalogoPrompt = ({ pieceName, processes, catalogSynced, requestedSync, onClose, onEditCatalog }) => {
  // Si el sync acaba de pasar y hay handler para editar catálogo,
  // el banner NO auto-cierra — Catalina debería poder ver el botón
  // y decidir si va a asignar máquinas/tarifas o lo deja para después.
  const persist = catalogSynced && !!onEditCatalog;
  useEffect(() => {
    if (persist) return undefined;
    const t = setTimeout(() => onClose?.(), catalogSynced ? 5000 : 12000);
    return () => clearTimeout(t);
  }, [catalogSynced, onClose, persist]);

  const procList = (processes || []).map((p) => p.name).join(" · ");

  let bg, border, color, msg;
  if (catalogSynced) {
    bg = "#0d2a15"; border = "#2ecc71"; color = "#2ecc71";
    msg = `Pipeline guardado y aplicado al catálogo de "${pieceName}". Próximas OTs lo van a heredar.`;
  } else if (requestedSync) {
    // Pidió sync pero no pasó (ej. catálogo ya tenía procesos definidos, o pieza es custom)
    bg = "#2d2000"; border = "#f39c12"; color = "#f39c12";
    msg = `Pipeline guardado solo para esta OT. El catálogo de "${pieceName}" ya tenía procesos previos — no se sobrescribió.`;
  } else {
    bg = "#1a1a1a"; border = "#2e2e2e"; color = "#888";
    msg = `Pipeline guardado para esta OT. Tip: la próxima vez podés tildar "guardar también en el catálogo" para que aplique a futuras OTs de esta pieza.`;
  }

  return (
    <div
      style={{
        margin: "0 clamp(8px, 2.5vw, 20px) 12px", // móvil: menos margen lateral
        padding: "10px 14px",
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 6,
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
      }}
    >
      <span style={{ fontSize: 14, color }}>{catalogSynced ? "✓" : "ℹ"}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: "#fff" }}>{msg}</div>
        {procList && (
          <div style={{ fontSize: 10, color: "#cbd5e1", marginTop: 3 }}>{procList}</div>
        )}
        {catalogSynced && onEditCatalog && (
          <div style={{ marginTop: 8, fontSize: 11, color: "#aaa" }}>
            ⚙ Los procesos quedaron sin máquina ni tarifa asignada — el cálculo
            del costo de proceso no funciona hasta que las completes.
            <button
              onClick={onEditCatalog}
              style={{
                marginLeft: 8,
                background: "#3498db",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "4px 10px",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Asignar máquinas y tarifas →
            </button>
          </div>
        )}
      </div>
      <button
        onClick={() => onClose?.()}
        style={{
          background: "transparent",
          border: "none",
          color: "#aaa",
          fontSize: 14,
          cursor: "pointer",
          lineHeight: 1,
          padding: 0,
        }}
      >
        ×
      </button>
    </div>
  );
};

// ─── ConfirmarAccion (Vista D) ─────────────────────────────────────────────
// Modal centrado, fondo overlay. SIN checkbox "no volver a preguntar" (D-T34).
export const ConfirmarAccion = ({ open, message, detail, onConfirm, onCancel }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#1a1a1a",
          border: "1px solid #2e2e2e",
          borderTop: "3px solid #f39c12",
          borderRadius: 6,
          minWidth: "min(320px, 92vw)", // móvil: no rebalsar el viewport
          maxWidth: "min(440px, 96vw)",
          padding: "16px 18px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16, color: "#f39c12" }}>⚠</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#fff", textTransform: "uppercase", letterSpacing: ".05em" }}>
            Confirmar acción
          </span>
        </div>
        <div style={{ fontSize: 13, color: "#fff" }}>{message}</div>
        {detail && <div style={{ fontSize: 11, color: "#cbd5e1" }}>{detail}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          <button
            onClick={onCancel}
            style={{
              background: "#1e1e1e",
              color: "#cbd5e1",
              border: "1px solid #333",
              borderRadius: 6,
              padding: "5px 12px",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            style={{
              background: "#f39c12",
              color: "#0d0d0d",
              border: "none",
              borderRadius: 6,
              padding: "5px 14px",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── UndoBanner (Vista E) ──────────────────────────────────────────────────
// Bottom-center fixed con countdown 30s. D-T35.
export const UndoBanner = ({ banner, onUndo, onClose, timeoutSec = 30 }) => {
  const [remainingMs, setRemainingMs] = useState(0);
  const expiresAt = banner?.expiresAt;

  useEffect(() => {
    if (!banner) return undefined;
    const tick = () => {
      const left = Math.max(0, (expiresAt || 0) - Date.now());
      setRemainingMs(left);
      if (left <= 0) {
        onClose?.();
      }
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [banner, expiresAt, onClose]);

  if (!banner) return null;
  const totalMs = timeoutSec * 1000;
  const pct = Math.max(0, Math.min(100, (remainingMs / totalMs) * 100));

  return (
    <div
      style={{
        position: "fixed",
        bottom: 20,
        left: "50%",
        transform: "translateX(-50%)",
        background: "#0d2a15",
        border: "1px solid #2ecc71",
        borderRadius: 6,
        padding: "10px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        minWidth: "min(360px, 92vw)", // móvil: no rebalsar el viewport
        maxWidth: "min(480px, 96vw)",
        zIndex: 999,
        boxShadow: "0 4px 20px rgba(0,0,0,.6)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 14, color: "#2ecc71" }}>✓</span>
        <div style={{ flex: 1, fontSize: 12, color: "#fff" }}>
          {banner.message}
        </div>
        <button
          onClick={onUndo}
          style={{
            background: "transparent",
            color: "#f39c12",
            border: "1px solid #f39c12",
            borderRadius: 6,
            padding: "4px 10px",
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          ↶ Deshacer
        </button>
        <button
          onClick={onClose}
          style={{
            background: "transparent",
            color: "#aaa",
            border: "none",
            fontSize: 14,
            lineHeight: 1,
            padding: 0,
            cursor: "pointer",
          }}
        >
          ×
        </button>
      </div>
      <div style={{ height: 3, background: "#0d4020", borderRadius: 2, overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            background: "#f39c12",
            transition: "width .25s linear",
          }}
        />
      </div>
    </div>
  );
};

// ─── InsertarProcesoEntre (Vista C) — deshabilitado, F4.4b ─────────────────
const InsertarProcesoEntre = ({ afterIndex }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        height: 14,
        margin: "2px 0",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        paddingLeft: 32,
        opacity: hover ? 1 : 0.3,
        transition: "opacity .15s",
      }}
      title="Disponible en F4.4b"
    >
      <span
        style={{
          fontSize: 9,
          color: "#999",
          fontStyle: "italic",
          cursor: "not-allowed",
          userSelect: "none",
        }}
      >
        + insertar acá (F4.4b)
      </span>
    </div>
  );
};

// ─── PipelineVertical interactivo ──────────────────────────────────────────
export const PipelineVertical = ({
  pi,
  onProcAction,
  onPipelineDefined,
  undoBannerProcIndex,
}) => {
  const procs = pi.processes || [];

  if (procs.length === 0) {
    return (
      <DefinirProcesosForm
        pi={pi}
        onSaved={(updatedPi, catalogSynced, requestedSync) =>
          onPipelineDefined?.(updatedPi, catalogSynced, requestedSync)
        }
      />
    );
  }

  return (
    <div style={{ padding: "12px 16px", borderTop: "1px dashed #2e2e2e", background: "#101010" }}>
      <div style={{ fontSize: 10, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>
        Pipeline de procesos
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        {procs.map((p, i) => {
          const done = !!p.completedAt;
          const inProgress = !done && !!p.startedAt;
          const isLast = i === procs.length - 1;
          const icon = done ? "✓" : inProgress ? "⚙" : "⏳";
          const bg = done ? "#2ecc71" : inProgress ? "#3498db" : "#0d0d0d";
          const fg = done || inProgress ? "#0d0d0d" : "#888";
          const border = done || inProgress ? "none" : "1px dashed #444";
          const canClick = !done || (done && undoBannerProcIndex === i);

          return (
            <React.Fragment key={p._id || i}>
              <div style={{ display: "flex", alignItems: "stretch", gap: 10 }}>
                <div className="proc-col" style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 22 }}>
                  <div
                    onClick={() => canClick && onProcAction?.(i, p, done, inProgress)}
                    title={canClick ? "Click para avanzar el estado" : "Sin acciones disponibles"}
                    className="proc-dot t-02"
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 11,
                      background: bg,
                      color: fg,
                      border,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 700,
                      flexShrink: 0,
                      cursor: canClick ? "pointer" : "default",
                      userSelect: "none",
                    }}
                  >
                    {icon}
                  </div>
                  {!isLast && (
                    <div style={{ flex: 1, width: 1, background: "#2e2e2e", marginTop: 2, marginBottom: 2 }} />
                  )}
                </div>

                <div style={{ paddingBottom: isLast ? 0 : 12, flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, color: "#fff", fontWeight: 600 }}>
                      {i + 1}. {p.name}
                    </span>
                    {p.isExternal && (
                      <span
                        style={{
                          fontSize: 9,
                          color: "#a78bfa",
                          background: "#a78bfa18",
                          border: "1px solid #a78bfa44",
                          padding: "1px 6px",
                          borderRadius: 9,
                        }}
                      >
                        externo{p.externalProviderName ? ` · ${p.externalProviderName}` : ""}
                      </span>
                    )}
                    {p.completedAt && (
                      <span style={{ fontSize: 10, color: "#aaa" }}>{fmtDateTime(p.completedAt)}</span>
                    )}
                  </div>
                  {p.notes && (
                    <div style={{ fontSize: 10, color: "#cbd5e1", marginTop: 2 }}>{p.notes}</div>
                  )}
                </div>
              </div>

              {!isLast && <InsertarProcesoEntre afterIndex={i} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// ─── DetalleOTs (agrupado por OC o por OT taller, items con PipelineVertical) ──
// Agrupa los PieceInstances por OC (clientOC) cuando hay, sino por número de OT.
// Misma estructura visual que DetalleOCs: header del grupo + items expandibles
// con su pipeline para definir/avanzar procesos.
const groupItems = (items, modo = "oc") => {
  const groupsMap = new Map();
  for (const pi of (items || [])) {
    const oc = (pi.workOrder?.clientOC || "").trim();
    const otNum = pi.workOrder?.number;
    // T2d (ago-26): modo "ot" agrupa SIEMPRE por N° de OT (el eje del taller);
    // la OC queda como chip informativo dentro del grupo.
    const key = (modo === "ot")
      ? `ot:${otNum ?? "?"}`
      : (oc ? `oc:${oc}` : `ot:${otNum ?? "?"}`);
    if (!groupsMap.has(key)) {
      groupsMap.set(key, {
        key,
        type: oc ? "oc" : "ot",
        oc: oc || null,
        otNumbers: new Set(),
        client: pi.workOrder?.clientName || null,
        // OTs internas (stock) no llevan cliente: mostramos el nombre de la
        // OT ("Stock interno · SKU") en su lugar.
        woName: pi.workOrder?.name || null,
        origin: pi.workOrder?.origin || null,
        quotationNumber: null, // 4-nov: cotización de origen (aprobada sin OC del cliente)
        items: [],
      });
    }
    const g = groupsMap.get(key);
    if (!g.quotationNumber && pi.workOrder?.quotationNumber) g.quotationNumber = pi.workOrder.quotationNumber;
    if (otNum != null) g.otNumbers.add(otNum);
    if (!g.client && pi.workOrder?.clientName) g.client = pi.workOrder.clientName;
    if (!g.woName && pi.workOrder?.name) g.woName = pi.workOrder.name;
    g.items.push(pi);
  }
  // Orden global por N° de OT desc (mayor primero, sin separar OC vs taller).
  return [...groupsMap.values()].sort((a, b) => {
    const ax = a.otNumbers.size ? Math.max(...a.otNumbers) : 0;
    const bx = b.otNumbers.size ? Math.max(...b.otNumbers) : 0;
    return bx - ax;
  });
};

export const DetalleOTs = ({
  items,
  loading,
  error,
  onFilterChange,
  currentFilter,
  onProcAction,
  onPipelineDefined,
  onSourcingChanged,
  onEditBOM,
  undoBanner,
}) => {
  const isMobile = useIsMobile(); // 26-ago: fila de grupo como tarjeta de 2 líneas
  const [openGroupKey, setOpenGroupKey] = useState(null);
  const [openItemId, setOpenItemId] = useState(null);
  const [procFilter, setProcFilter] = useState(null); // clip por proceso/estación
  // T2d: agrupación OC (histórica) u OT (eje del taller) — preferencia persistente.
  const [groupMode, setGroupMode] = useState(
    () => localStorage.getItem("produccionGroupMode") || "oc"
  );
  const cambiarGroupMode = (m) => {
    setGroupMode(m);
    try { localStorage.setItem("produccionGroupMode", m); } catch { /* sin storage */ }
  };

  if (loading && (!items || items.length === 0)) {
    return <div className="prod-expand" style={expandWrap}><div style={stateMessage()}>Cargando piezas activas…</div></div>;
  }
  if (error) {
    return <div className="prod-expand" style={expandWrap}><div style={stateMessage("#e74c3c")}>Error: {error}</div></div>;
  }

  const total = items?.length || 0;
  const counts = (items || []).reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, {});

  const filterChip = (key, label, color) => {
    const active = currentFilter === key || (!currentFilter && key === "all");
    return (
      <span
        key={key}
        className="chip-touch t-02"
        onClick={() => onFilterChange?.(key === "all" ? null : key)}
        style={chipStyle(active, "#fff", color)}
      >
        {label}
      </span>
    );
  };

  // ── Clips POR PROCESO/ESTACIÓN (filtro local sobre las piezas cargadas) ──────
  // "Paso actual" de una pieza = el proceso en curso, o el primero sin completar.
  // Cada clip = una estación; al activarlo se ven solo las piezas que están ahí.
  // Catalina abre la pieza y la va avanzando (tickeando) por su pipeline; al
  // completar el paso, la pieza salta sola al clip de la estación siguiente.
  const currentProc = (pi) => {
    const procs = pi.processes || [];
    if (!procs.length) return { key: "__sin__", label: "Sin procesos", order: 9998 };
    const inProg = procs.find((p) => p.status === "in_progress");
    const next = inProg || procs.find((p) => !p.completedAt && p.status !== "done");
    if (!next) return null; // todos los procesos hechos
    // Clave case-insensitive: "TORNO" y "Torno" son la misma estación
    // (el backend ya canoniza al guardar; esto cubre datos previos).
    return { key: next.name.trim().toLowerCase(), label: next.name, order: next.order || 0 };
  };
  const stationMap = new Map();
  for (const pi of items || []) {
    const cp = currentProc(pi);
    if (!cp) continue;
    const e = stationMap.get(cp.key) || { label: cp.label, order: cp.order, count: 0 };
    e.count += 1;
    e.order = Math.min(e.order, cp.order);
    stationMap.set(cp.key, e);
  }
  // Estaciones ordenadas por su posición en el pipeline; "Sin procesos" al final.
  const stations = [...stationMap.entries()].sort((a, b) => a[1].order - b[1].order);
  const shownItems = procFilter
    ? (items || []).filter((pi) => { const cp = currentProc(pi); return cp && cp.key === procFilter; })
    : (items || []);
  const groups = groupItems(shownItems, groupMode);

  return (
    <div className="prod-expand" style={expandWrap}>
      <div className="chips-scroll" style={filterRow}>
        {filterChip("all", `Todas (${total})`, "#fff")}
        {filterChip("pending", `Pendiente (${counts.pending || 0})`, "#cbd5e1")}
        {filterChip("in_process", `En proceso (${counts.in_process || 0})`, "#3498db")}
        {filterChip("ready", `Lista (${counts.ready || 0})`, "#2ecc71")}
        {/* T2d: toggle de agrupación — el taller trabaja por OT */}
        <span style={{ marginLeft: "auto", display: "inline-flex", gap: 4, alignItems: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 10, color: "#777", textTransform: "uppercase", letterSpacing: ".06em" }}>Agrupar</span>
          <span className="chip-touch t-02" onClick={() => cambiarGroupMode("oc")} style={chipStyle(groupMode === "oc", "#fff", "#cbd5e1")}>OC</span>
          <span className="chip-touch t-02" onClick={() => cambiarGroupMode("ot")} style={chipStyle(groupMode === "ot", "#fff", "#f39c12")}>OT</span>
        </span>
      </div>

      {stations.length > 0 && (
        <div className="chips-scroll" style={{ ...filterRow, borderTop: "none" }}>
          <span style={{ fontSize: 10, color: "#777", alignSelf: "center", marginRight: 2, textTransform: "uppercase", letterSpacing: ".06em", whiteSpace: "nowrap" }}>
            Por proceso:
          </span>
          {stations.map(([key, s]) => (
            <span
              key={key}
              className="chip-touch t-02"
              onClick={() => setProcFilter(procFilter === key ? null : key)}
              style={chipStyle(procFilter === key, "#fff", key === "__sin__" ? "#e67e22" : "#8b5cf6")}
            >
              {s.label} ({s.count})
            </span>
          ))}
          {procFilter && (
            <span onClick={() => setProcFilter(null)} style={{ alignSelf: "center", marginLeft: 4, cursor: "pointer", color: "#999", fontSize: 11 }}>
              ✕ limpiar
            </span>
          )}
        </div>
      )}

      {(!items || items.length === 0) ? (
        <div style={stateMessage()}>
          No hay piezas activas{currentFilter ? ` con filtro "${currentFilter}"` : ""}
        </div>
      ) : shownItems.length === 0 ? (
        <div style={stateMessage()}>
          No hay piezas en “{stationMap.get(procFilter)?.label || procFilter}”.
        </div>
      ) : (
        <div>
          {groups.map((g) => {
            const isGroupOpen = openGroupKey === g.key;
            const groupItemsCount = g.items.length;
            // Resumen procesos del grupo
            const groupReady = g.items.filter(pi => {
              const procs = pi.processes || [];
              return procs.length > 0 && procs.every(p => p.completedAt);
            }).length;
            const pct = groupItemsCount > 0 ? Math.round((groupReady / groupItemsCount) * 100) : 0;
            const otsList = [...g.otNumbers].sort((a, b) => a - b);
            return (
              <div key={g.key} style={{ borderBottom: "1px solid #1f1f1f" }}>
                {/* Header del grupo */}
                <div
                  onClick={() => setOpenGroupKey(isGroupOpen ? null : g.key)}
                  className="t-02 tap-row"
                  style={{
                    background: isGroupOpen ? "#1f1f24" : "#1a1a1a",
                    padding: isMobile ? "10px 12px" : "8px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: isMobile ? 8 : 12,
                    rowGap: 6,
                    // móvil (26-ago): tarjeta de 2 líneas — badges+avance arriba,
                    // cliente COMPLETO abajo (antes se cortaba en "Roc…")
                    flexWrap: "wrap",
                    cursor: "pointer",
                    borderBottom: isGroupOpen ? "1px solid #2e2e2e" : "none",
                  }}
                >
                  <span style={{ fontSize: 12, color: "#999", width: 12 }}>
                    {isGroupOpen ? "▼" : "▶"}
                  </span>

                  {/* OT badge — primer/último OT del grupo */}
                  {otsList.length > 0 && (
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#f39c12",
                        background: "#f39c1218",
                        border: "1px solid #f39c1244",
                        padding: "2px 7px",
                        borderRadius: 4,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {otsList.length === 1
                        ? `OT ${otsList[0]}`
                        : `OT ${otsList[0]}…${otsList[otsList.length - 1]}`}
                    </span>
                  )}

                  {/* OC badge, cotización de origen (aprobada sin OC del cliente) o taller badge */}
                  {g.quotationNumber && (g.type !== "oc" || /^\s*sin\s+oc/i.test(g.oc || "")) ? (
                    <span
                      title={`Aprobada desde la cotización N° ${g.quotationNumber}, sin OC del cliente`}
                      style={{
                        fontFamily: "monospace",
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#a78bfa",
                        background: "#a78bfa18",
                        border: "1px solid #a78bfa44",
                        padding: "2px 7px",
                        borderRadius: 4,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Cot. N° {g.quotationNumber}
                    </span>
                  ) : g.type === "oc" ? (
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#3498db",
                        background: "#3498db18",
                        border: "1px solid #3498db44",
                        padding: "2px 7px",
                        borderRadius: 4,
                        whiteSpace: "nowrap",
                      }}
                    >
                      N° OC {g.oc}
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#a78bfa",
                        background: "#a78bfa18",
                        border: "1px solid #a78bfa44",
                        padding: "2px 7px",
                        borderRadius: 4,
                        whiteSpace: "nowrap",
                      }}
                    >
                      taller · sin OC
                    </span>
                  )}

                  {/* Avance — en móvil sube a la primera línea, a la derecha */}
                  {isMobile && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
                      <div className="prod-bar" style={{ width: 48, height: 5, background: "#2a2a2a", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? "#2ecc71" : "#f39c12" }} />
                      </div>
                      <span style={{ fontSize: 11, color: pct === 100 ? "#2ecc71" : "#f39c12", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                        {groupReady}/{groupItemsCount}
                      </span>
                    </div>
                  )}

                  {/* Cliente — móvil: línea 2 completa (nombre entero); desktop: al medio */}
                  <div style={{
                    flex: isMobile ? "1 1 100%" : 1,
                    minWidth: 0,
                    ...(isMobile ? { display: "flex", alignItems: "baseline", gap: 8, paddingLeft: 22 } : {}),
                  }}>
                    <span
                      style={{
                        fontSize: isMobile ? 13 : 12,
                        color: "#fff",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        ...(isMobile ? { flex: 1, minWidth: 0, fontWeight: 600 } : {}),
                      }}
                    >
                      {g.client
                        || (g.woName && (
                          <span style={{ color: "#e2b96f" }}>
                            {g.origin === "interno" ? "🏭 " : ""}{g.woName}
                          </span>
                        ))
                        || <i style={{ color: "#999" }}>sin cliente</i>}
                    </span>
                    {isMobile && (
                      <span style={{ fontSize: 11, color: "#8a8a8a", whiteSpace: "nowrap", flexShrink: 0 }}>
                        {groupItemsCount} item{groupItemsCount === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>

                  {/* Desktop: items + avance al final de la única línea */}
                  {!isMobile && (
                    <>
                      <span style={{ fontSize: 11, color: "#cbd5e1", whiteSpace: "nowrap", fontWeight: 500 }}>
                        {groupItemsCount} item{groupItemsCount === 1 ? "" : "s"}
                      </span>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 90 }}>
                        <div className="prod-bar" style={{ width: 60, height: 4, background: "#2a2a2a", borderRadius: 2, overflow: "hidden" }}>
                          <div
                            style={{
                              height: "100%",
                              width: `${pct}%`,
                              background: pct === 100 ? "#2ecc71" : "#f39c12",
                            }}
                          />
                        </div>
                        <span style={{ fontSize: 10, color: pct === 100 ? "#2ecc71" : "#f39c12", fontWeight: 700 }}>
                          {groupReady}/{groupItemsCount}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {/* Items del grupo */}
                {isGroupOpen && (
                  <div style={{ background: "#101010" }}>
                    {g.items.map((pi) => {
                      const isItemOpen = openItemId === pi._id;
                      const procs = pi.processes || [];
                      const procCount = procs.length;
                      const procDone = procs.filter((p) => p.completedAt).length;
                      const inProgress = procs.find((p) => !p.completedAt && p.startedAt);
                      const isAssembly = (pi.childrenCount || 0) > 0;

                      let resumen;
                      if (isAssembly) {
                        const totalChildren = pi.childrenCount;
                        const readyChildren = pi.childrenReady || 0;
                        resumen = (
                          <span style={{ fontSize: 10, color: readyChildren === totalChildren ? "#2ecc71" : "#a78bfa", fontWeight: 700 }}>
                            🧩 Conjunto ({readyChildren}/{totalChildren})
                          </span>
                        );
                      } else if (procCount === 0) {
                        resumen = <span style={{ fontSize: 10, color: "#f39c12" }}>⚠ sin procesos</span>;
                      } else if (procDone === procCount) {
                        resumen = <span style={{ fontSize: 10, color: "#2ecc71", fontWeight: 700 }}>✓ lista p/entrega</span>;
                      } else if (inProgress) {
                        resumen = (
                          <span style={{ fontSize: 10, color: "#3498db", fontWeight: 700 }}>
                            ⚙ {inProgress.name} ({procDone}/{procCount})
                          </span>
                        );
                      } else {
                        resumen = (
                          <span style={{ fontSize: 10, color: "#cbd5e1" }}>
                            ⏳ pendiente ({procDone}/{procCount})
                          </span>
                        );
                      }

                      const undoIdxForThisPi = undoBanner?.piId === String(pi._id) ? undoBanner.procIndex : null;

                      return (
                        <div key={pi._id} style={{ borderTop: "1px solid #1a1a1a" }}>
                          <div
                            onClick={() => setOpenItemId(isItemOpen ? null : pi._id)}
                            className="t-02 tap-row"
                            style={{
                              padding: "8px 12px 8px 32px",
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              rowGap: 6,
                              flexWrap: "wrap", // móvil: chips/acciones bajan de línea
                              cursor: "pointer",
                              background: isItemOpen ? "#181820" : "transparent",
                            }}
                          >
                            <span style={{ fontSize: 12, color: "#999", width: 12 }}>{isItemOpen ? "▼" : "▶"}</span>

                            <span
                              style={{
                                fontFamily: "monospace",
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#f39c12",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {pi.subIdIndex != null && pi.workOrder?.number != null
                                ? formatItemSubId(pi.workOrder.number, pi.subIdIndex)
                                : `OT N° ${pi.workOrder?.number || "—"}`}
                            </span>

                            <span style={{ fontSize: 12, color: "#fff", flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                              {(pi.piece?.sku || pi.sku) && (
                                <span style={{ fontFamily: "monospace", color: "#3498db", marginRight: 8 }}>{pi.piece?.sku || pi.sku}</span>
                              )}
                              {pi.piece?.name || pi.pieceNameSnapshot || <i style={{ color: "#999" }}>sin nombre</i>}
                              {pi.quantityPending != null && pi.quantityPending < pi.quantity ? (
                                <span style={{ color: "#cbd5e1", marginLeft: 8, fontWeight: 500 }}>
                                  · quedan {pi.quantityPending} de {pi.quantity}
                                  <span style={{ color: "#2ecc71", marginLeft: 6, fontSize: 11 }}>
                                    ({(pi.quantityInvoiced || 0) > 0 ? `${pi.quantityInvoiced} facturada${pi.quantityInvoiced === 1 ? "" : "s"}` : ""}
                                    {(pi.quantityInvoiced || 0) > 0 && (pi.quantityDelivered || 0) > 0 ? " + " : ""}
                                    {(pi.quantityDelivered || 0) > 0 ? `${pi.quantityDelivered} entregada${pi.quantityDelivered === 1 ? "" : "s"}` : ""})
                                  </span>
                                </span>
                              ) : (
                                <span style={{ color: "#cbd5e1", marginLeft: 8, fontWeight: 500 }}>· {pi.quantity}×</span>
                              )}
                            </span>

                            {/* T2b — planificación: bodega al lado de cada pieza + acciones.
                                "+stock" aplica a CUALQUIER pieza de catálogo (fabricar extra
                                activa la gestión de stock sola); chip y "Cubrir" solo con
                                gestión activa. Caso amortiguadores 13-ago. */}
                            {pi.piece && (() => {
                              const gestionada = !!pi.piece.managedStock;
                              const pend = pi.quantityPending != null ? pi.quantityPending : pi.quantity;
                              const hayStock = gestionada && (pi.piece.stock || 0) >= pend && pend > 0;
                              const esInterna = pi.workOrder?.origin === "interno";
                              return (
                                <span style={{ display: "inline-flex", gap: 5, alignItems: "center", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                                  {gestionada && (
                                  <span
                                    title={`En bodega: ${pi.piece.stock || 0} · punto de reposición: ${pi.piece.reorderPoint || 0}`}
                                    style={{
                                      fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 99,
                                      color: hayStock ? "#2ecc71" : (pi.piece.stock || 0) > 0 ? "#f39c12" : "#777",
                                      border: `1px solid ${hayStock ? "#2ecc71" : (pi.piece.stock || 0) > 0 ? "#f39c12" : "#555"}55`,
                                      background: "#00000030",
                                    }}
                                  >
                                    📦 {pi.piece.stock || 0}
                                  </span>
                                  )}
                                  {hayStock && pi.status === "pending" && !esInterna && (
                                    <button
                                      title={`Cubrir ${pend} unidad(es) desde bodega — descuenta stock y deja la pieza lista sin fabricar`}
                                      onClick={async () => {
                                        if (!window.confirm(`¿Cubrir ${pend} × ${pi.piece?.name || pi.pieceNameSnapshot} desde bodega? El stock queda en ${(pi.piece.stock || 0) - pend}.`)) return;
                                        try {
                                          await backend.patch(`pieceInstance/${pi._id}/sourcing`, { decision: "stock" });
                                          window.location.reload();
                                        } catch (err) {
                                          alert(err?.response?.data?.message || "No se pudo cubrir desde stock");
                                        }
                                      }}
                                      style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 5, cursor: "pointer", background: "#0d2a15", color: "#2ecc71", border: "1px solid #0d4020" }}
                                    >
                                      Cubrir de bodega
                                    </button>
                                  )}
                                  {!esInterna && (
                                    <button
                                      title="Fabricar unidades EXTRA a stock (crea OT interna hermana — el extra deja de ser fantasma)"
                                      onClick={async () => {
                                        const n = window.prompt(`¿Cuántas unidades EXTRA de ${pi.piece?.name || pi.pieceNameSnapshot} fabricar para stock?`, "1");
                                        const qty = Number(n);
                                        if (!n || !Number.isFinite(qty) || qty <= 0) return;
                                        try {
                                          const r = await backend.post("workOrder/from-stock-alert", {
                                            pieceId: pi.piece._id,
                                            qty,
                                            relatedWorkOrder: pi.workOrder?._id || null,
                                            motivo: `Extra a stock decidido en planificación (junto a OT ${pi.workOrder?.number || "?"}).`,
                                          });
                                          alert(`OT interna N°${r.data?.number} creada: ${qty} × a stock.`);
                                          window.location.reload();
                                        } catch (err) {
                                          alert(err?.response?.data?.error || "No se pudo crear la OT interna");
                                        }
                                      }}
                                      style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 5, cursor: "pointer", background: "#1a1a2e", color: "#a085f0", border: "1px solid #a085f044" }}
                                    >
                                      +stock
                                    </button>
                                  )}
                                </span>
                              );
                            })()}
                            {pi.workOrder?.origin === "interno" && (
                              <span style={{ fontSize: 9.5, fontWeight: 700, color: "#a085f0", border: "1px solid #a085f044", background: "#a085f018", padding: "1px 6px", borderRadius: 3, whiteSpace: "nowrap" }}>
                                a stock
                              </span>
                            )}

                            <span style={{ minWidth: 160, textAlign: "right" }}>{resumen}</span>
                          </div>

                          {isItemOpen && (
                            isAssembly ? (
                              <AssemblySubPIs
                                parent={pi}
                                onProcAction={onProcAction}
                                onPipelineDefined={onPipelineDefined}
                                onSourcingChanged={onSourcingChanged}
                                onEditBOM={onEditBOM}
                              />
                            ) : (
                              <PipelineVertical
                                pi={pi}
                                onProcAction={(idx, p, done, inProgress) =>
                                  onProcAction?.(pi, idx, p, done, inProgress)
                                }
                                onPipelineDefined={(updated, catalogSynced, requestedSync) =>
                                  onPipelineDefined?.(pi, updated, catalogSynced, requestedSync)
                                }
                                undoBannerProcIndex={undoIdxForThisPi}
                              />
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── AssemblySubPIs ────────────────────────────────────────────────────────
// BOM Fase 3 — UI cuando una PI es un conjunto (childrenCount > 0).
// Lista los sub-componentes; cada uno tiene su toggle "Stock" / "Fabricar".
// Si "Stock": descuenta inventario al confirmar, marca sub-PI como ready.
// Si "Fabricar": muestra PipelineVertical normal con sus procesos.
// Si el conjunto tiene procesos propios (ensamble final), muestra pipeline del
// padre debajo de los componentes.
export const AssemblySubPIs = ({ parent, onProcAction, onPipelineDefined, onSourcingChanged, onEditBOM }) => {
  const [children, setChildren] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyIds, setBusyIds] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [resyncPreview, setResyncPreview] = useState(null); // { plan } | null
  const [resyncBusy, setResyncBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await backend.get(`pieceInstance/${parent._id}/children`);
      setChildren(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [parent._id]);

  const previewResync = async () => {
    setResyncBusy(true);
    try {
      const res = await backend.post(`pieceInstance/${parent._id}/resync-bom`, { dryRun: true });
      setResyncPreview(res.data.plan);
    } catch (e) {
      alert("No se pudo previsualizar resync: " + (e?.response?.data?.message || e.message));
    }
    setResyncBusy(false);
  };

  const applyResync = async () => {
    setResyncBusy(true);
    try {
      await backend.post(`pieceInstance/${parent._id}/resync-bom`, { dryRun: false });
      setResyncPreview(null);
      await load();
      onSourcingChanged?.();
    } catch (e) {
      alert("Error al aplicar resync: " + (e?.response?.data?.message || e.message));
    }
    setResyncBusy(false);
  };

  const setSourcing = async (childId, decision) => {
    setBusyIds(prev => new Set(prev).add(childId));
    try {
      const res = await backend.patch(`pieceInstance/${childId}/sourcing`, { decision });
      setChildren(prev => (prev || []).map(c => c._id === childId ? { ...c, ...res.data.pieceInstance } : c));
      onSourcingChanged?.();
    } catch (e) {
      const msg = e?.response?.data?.message || e.message;
      alert("No se pudo cambiar el sourcing: " + msg);
    }
    setBusyIds(prev => { const n = new Set(prev); n.delete(childId); return n; });
  };

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  if (loading) {
    return <div style={{ padding: "10px 32px", fontSize: 11, color: "#cbd5e1" }}>Cargando componentes…</div>;
  }
  if (error) {
    return <div style={{ padding: "10px 32px", fontSize: 11, color: "#e74c3c" }}>Error: {error}</div>;
  }
  if (!children || children.length === 0) {
    return <div style={{ padding: "10px 32px", fontSize: 11, color: "#cbd5e1" }}>Este conjunto no tiene componentes definidos.</div>;
  }

  const readyCount = children.filter(c => ["ready", "delivered", "invoiced"].includes(c.status)).length;
  const parentHasOwnProcs = (parent.processes_count || (parent.processes || []).length) > 0;
  const parentPieceId = parent.piece?._id || null;

  return (
    <div style={{ background: "#0d0d12", padding: "10px 12px 14px 32px", borderTop: "1px dashed #2e2e2e" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: "#a78bfa", fontWeight: 600, textTransform: "uppercase",
          letterSpacing: ".05em" }}>
          🧩 Componentes del conjunto · {readyCount}/{children.length} listos
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={previewResync}
            disabled={resyncBusy}
            style={{
              background: "#1e1e1e", color: "#3498db", border: "1px solid #333",
              borderRadius: 6, padding: "3px 10px", fontSize: 10,
              cursor: resyncBusy ? "wait" : "pointer",
            }}
            title="Reconciliar componentes/procesos con el catálogo actual (sin pisar lo ya empezado)"
          >
            🔄 Resync con catálogo
          </button>
          {onEditBOM && parentPieceId && (
            <button
              onClick={() => onEditBOM(parentPieceId)}
              style={{
                background: "#1e1e1e", color: "#a78bfa", border: "1px solid #333",
                borderRadius: 6, padding: "3px 10px", fontSize: 10, cursor: "pointer",
              }}
              title="Abrir el editor de catálogo para modificar componentes/procesos"
            >
              ✎ Editar BOM/procesos →
            </button>
          )}
        </div>
      </div>

      {/* Modal preview de resync */}
      {resyncPreview && (
        <ResyncPreviewModal
          plan={resyncPreview}
          onCancel={() => setResyncPreview(null)}
          onConfirm={applyResync}
          busy={resyncBusy}
        />
      )}
      {children.map((c) => {
        const isOpen = expanded.has(c._id);
        const decision = c.sourcingDecision; // null | "stock" | "fabricar"
        const stockAvail = c.piece?.stock || 0;
        const canStock = !!c.piece?.managedStock;
        const busy = busyIds.has(c._id);
        const procs = c.processes || [];
        const procDone = procs.filter(p => p.completedAt).length;
        const isReady = c.status === "ready";

        return (
          <div key={c._id} style={{ borderTop: "1px solid #1a1a1a", padding: "6px 0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, rowGap: 6, flexWrap: "wrap" /* móvil */ }}>
              <button onClick={() => toggleExpand(c._id)}
                style={{ background: "transparent", border: "none", color: "#999", fontSize: 11, cursor: "pointer", width: 14 }}>
                {isOpen ? "▼" : "▶"}
              </button>
              <span style={{ fontFamily: "monospace", fontSize: 11, color: "#3498db", minWidth: 110 }}>
                {c.piece?.sku || "—"}
              </span>
              <span style={{ fontSize: 12, color: "#ddd", flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                {c.piece?.name || c.pieceNameSnapshot}
                <span style={{ color: "#aaa", marginLeft: 6 }}>· {c.quantity}×</span>
              </span>
              {isReady ? (
                <span style={{ fontSize: 10, color: "#2ecc71", fontWeight: 700 }}>
                  ✓ {decision === "stock" ? "tomado de stock" : "lista"}
                </span>
              ) : decision === "fabricar" ? (
                <span style={{ fontSize: 10, color: "#3498db" }}>
                  ⚙ fabricando ({procDone}/{procs.length})
                </span>
              ) : (
                <span style={{ fontSize: 10, color: "#aaa" }}>
                  decisión pendiente
                </span>
              )}
              {!isReady && (
                <div style={{ display: "flex", gap: 4 }}>
                  <button
                    disabled={!canStock || busy || stockAvail < c.quantity}
                    onClick={() => setSourcing(c._id, "stock")}
                    title={!canStock ? "La pieza no tiene gestión de stock activada" : stockAvail < c.quantity ? `Stock insuficiente (${stockAvail}/${c.quantity})` : "Tomar de stock"}
                    style={{
                      background: decision === "stock" ? "#0a1f15" : "#1f1f1f",
                      color: decision === "stock" ? "#2ecc71" : (!canStock || stockAvail < c.quantity ? "#444" : "#aaa"),
                      border: `1px solid ${decision === "stock" ? "#2ecc7140" : "#3a3a3a"}`,
                      borderRadius: 6, padding: "3px 8px", fontSize: 10,
                      cursor: (canStock && stockAvail >= c.quantity && !busy) ? "pointer" : "not-allowed",
                    }}>
                    Stock {canStock ? `(${stockAvail})` : ""}
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => setSourcing(c._id, "fabricar")}
                    style={{
                      background: decision === "fabricar" ? "#0d1e2e" : "#1f1f1f",
                      color: decision === "fabricar" ? "#3498db" : "#aaa",
                      border: `1px solid ${decision === "fabricar" ? "#3498db40" : "#3a3a3a"}`,
                      borderRadius: 6, padding: "3px 8px", fontSize: 10,
                      cursor: busy ? "wait" : "pointer",
                    }}>
                    Fabricar
                  </button>
                </div>
              )}
            </div>
            {isOpen && decision === "fabricar" && (
              <div style={{ marginTop: 6 }}>
                <PipelineVertical
                  pi={c}
                  onProcAction={(idx, p, done, inProgress) => onProcAction?.(c, idx, p, done, inProgress)}
                  onPipelineDefined={(updated, catalogSynced, requestedSync) =>
                    onPipelineDefined?.(c, updated, catalogSynced, requestedSync)}
                />
              </div>
            )}
            {isOpen && decision === "stock" && (
              <div style={{ fontSize: 11, color: "#cbd5e1", padding: "6px 0 6px 24px" }}>
                Tomado de stock. No requiere fabricación. Stock descontado del inventario.
              </div>
            )}
            {isOpen && !decision && (
              <div style={{ fontSize: 11, color: "#aaa", padding: "6px 0 6px 24px" }}>
                Elegí <strong>Stock</strong> o <strong>Fabricar</strong> arriba para avanzar este componente.
              </div>
            )}
          </div>
        );
      })}

      {/* Pipeline propio del padre (ensamble final). Solo se muestra si el
          conjunto tiene procesos en Piece.processes (ej. "ensamblar HOUSE").
          Nota UX: hoy el ensamble se puede arrancar aunque las sub-PIs no
          estén ready. Si querés bloquear hasta que todos los componentes
          estén listos, hay que agregar lógica en backend. */}
      {parentHasOwnProcs && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px dashed #2e2e2e" }}>
          <div style={{ fontSize: 11, color: "#f39c12", fontWeight: 600, textTransform: "uppercase",
            letterSpacing: ".05em", marginBottom: 6 }}>
            ⚙ Ensamble final del conjunto
            {readyCount < children.length && (
              <span style={{ marginLeft: 8, fontSize: 10, color: "#cbd5e1", fontWeight: 400, textTransform: "none" }}>
                — bloqueado: faltan {children.length - readyCount} componente(s) por terminar
              </span>
            )}
          </div>
          <PipelineVertical
            pi={parent}
            onProcAction={(idx, p, done, inProgress) => onProcAction?.(parent, idx, p, done, inProgress)}
            onPipelineDefined={(updated, catalogSynced, requestedSync) =>
              onPipelineDefined?.(parent, updated, catalogSynced, requestedSync)}
          />
        </div>
      )}
    </div>
  );
};

// ─── ResyncPreviewModal ────────────────────────────────────────────────────
// Modal de confirmación para el resync BOM. Muestra qué se va a crear, actualizar,
// saltear (ya en sync o ya empezado), y huérfanos (sub-PIs cuyo componente
// ya no está en el catálogo — NO se borran, sólo se reportan).
const ResyncPreviewModal = ({ plan, onCancel, onConfirm, busy }) => {
  const totalActions = plan.creates.length + plan.updates.length;
  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#1a1a1a", border: "1px solid #2e2e2e", borderRadius: 8,
          padding: 22, maxWidth: 680, width: "100%", maxHeight: "85vh",
          overflow: "auto", color: "#ddd",
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 4 }}>
          🔄 Resync con catálogo
        </div>
        <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 14 }}>
          Reconciliación entre las sub-PIs existentes y el BOM/procesos actuales del catálogo.
          Las sub-PIs que ya arrancaron o están listas NO se modifican.
        </div>

        {plan.creates.length > 0 && (
          <Section title={`A crear (${plan.creates.length})`} color="#2ecc71">
            {plan.creates.map((c, i) => (
              <div key={i} style={rowSt}>
                <span className="bini-mono" style={{ color: "#3498db", minWidth: 110 }}>{c.componentSku}</span>
                <span style={{ flex: 1 }}>{c.componentName}</span>
                <span style={{ color: "#aaa" }}>qty={c.quantity} · {c.processes.length} proc(s)</span>
              </div>
            ))}
          </Section>
        )}

        {plan.updates.length > 0 && (
          <Section title={`A actualizar (${plan.updates.length})`} color="#3498db">
            {plan.updates.map((u, i) => (
              <div key={i} style={rowSt}>
                <span className="bini-mono" style={{ color: "#3498db", minWidth: 110 }}>{u.componentSku}</span>
                <span style={{ flex: 1, fontSize: 11, color: "#aaa" }}>
                  {Object.entries(u.changes).map(([k, v]) => {
                    if (typeof v === "object" && v.from !== undefined) {
                      return `${k}: ${v.from} → ${v.to}`;
                    }
                    return `${k}: ${v.fromCount} → ${v.toCount} procs`;
                  }).join(" · ")}
                </span>
              </div>
            ))}
          </Section>
        )}

        {plan.skips.length > 0 && (
          <Section title={`Sin cambios (${plan.skips.length})`} color="#666">
            <div style={{ fontSize: 10, color: "#aaa" }}>
              {plan.skips.filter(s => s.reason === "already_started").length} ya empezadas /
              {plan.skips.filter(s => s.reason === "in_sync").length} ya en sync
            </div>
          </Section>
        )}

        {plan.orphans.length > 0 && (
          <Section title={`⚠ Huérfanos (${plan.orphans.length})`} color="#f39c12">
            <div style={{ fontSize: 11, color: "#aaa", marginBottom: 6 }}>
              Sub-PIs cuyo componente ya no está en el catálogo (o cambió de pieza referenciada).
              NO se borran automáticamente. Revisalas manualmente.
            </div>
            {plan.orphans.map((o, i) => (
              <div key={i} style={rowSt}>
                <span style={{ minWidth: 110, fontSize: 11, color: "#cbd5e1" }}>idx {o.subIndex ?? "?"}</span>
                <span style={{ flex: 1, fontSize: 11, color: "#aaa" }}>
                  {o.reason === "out_of_range" ? "Componente removido del catálogo" :
                   o.reason === "piece_mismatch" ? "Pieza distinta en ese slot" : o.reason}
                  {o.status && ` · status: ${o.status}`}
                </span>
              </div>
            ))}
          </Section>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button onClick={onCancel} disabled={busy}
            style={{
              background: "#1e1e1e", color: "#cbd5e1", border: "1px solid #333",
              borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer",
            }}>
            Cancelar
          </button>
          <button onClick={onConfirm} disabled={busy || totalActions === 0}
            style={{
              background: totalActions === 0 ? "#1f1f1f" : "#27ae60",
              color: totalActions === 0 ? "#666" : "#fff",
              border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12,
              fontWeight: 700,
              cursor: (busy || totalActions === 0) ? "not-allowed" : "pointer",
            }}>
            {busy ? "Aplicando…" : totalActions === 0 ? "Nada que aplicar" : `Aplicar (${totalActions})`}
          </button>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, color, children }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 11, color, fontWeight: 700, textTransform: "uppercase",
      letterSpacing: ".05em", marginBottom: 4 }}>{title}</div>
    <div style={{ background: "#101010", border: "1px solid #2a2a2a", borderRadius: 4, padding: "6px 10px" }}>
      {children}
    </div>
  </div>
);

const rowSt = {
  display: "flex", alignItems: "center", gap: 8, padding: "3px 0", fontSize: 12,
};
